import React, { useEffect, useRef } from "react";
import { isBoolean } from "lodash";
import styled from "styled-components";

import {
	StyledSpan,
	StyledP,
	CheckBoxWithText,
	StyledColumn,
	Colors,
} from "../../../../common";
import { useAppContext } from "../../../../../context";
import ListSelect from "..";

const List = styled(StyledColumn)`
	width: 100%;
	max-height: 435px;
	overflow-y: scroll;

	& > *:not(:last-child) {
		border-bottom: 1px solid #c8cfd6;
	}
`;

const Item = styled(StyledP)<{
	active: boolean;
}>`
	padding: 16px 5px;
	gap: 16px;
	color: #21333f;
	background-color: ${(props) => (props.active ? "#ebebeb" : "#ffffff")};

	&:hover {
		background-color: #ebebeb;
	}
`;

const ListCheckBoxes: React.FC<ListCheckBoxes.Props> = ({
	options,
	activeOption,
	selected,
	showActiveItem,
	translateText,
	selectOnlyOne,
	setSelected,
	setWarning,
	handleSelect,
	onScrollMaxVisible,
	showTaxiServices = false,
	showCompanies = false,
}) => {
	const {
		getTaxiServiceName,
		getCompanyIdByTaxiServiceId,
		getCompanyName,
		language,
	} = useAppContext();

	const listRef = useRef<HTMLDivElement | null>(null);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	useEffect(() => {
		if (activeOption === -1) return;

		const isLastOption = options.length === activeOption;
		const focusOptionNextView = isLastOption ? 1 : 0;
		const currentItem =
			itemRefs.current[activeOption - focusOptionNextView];

		if (currentItem) {
			currentItem.scrollIntoView({
				block: "nearest",
				inline: "nearest",
				behavior: "auto",
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeOption]);

	useEffect(() => {
		if (!listRef.current || !onScrollMaxVisible) return () => {};

		const handleScroll = () => {
			const container = listRef.current;
			if (!container) return;

			const { scrollTop, clientHeight } = container;
			let maxIndex = -1;

			itemRefs.current.forEach((el, i) => {
				if (!el) return;

				const { offsetTop, offsetHeight } = el;

				const isVisible =
					offsetTop + offsetHeight > scrollTop &&
					offsetTop < scrollTop + clientHeight;

				if (isVisible) {
					maxIndex = Math.max(maxIndex, i);
				}
			});

			if (maxIndex !== -1 && onScrollMaxVisible) {
				onScrollMaxVisible(maxIndex);
			}
		};

		const container = listRef.current;
		container.addEventListener("scroll", handleScroll);

		return () => {
			container.removeEventListener("scroll", handleScroll);
		};
	}, [onScrollMaxVisible]);

	return (
		<List
			ref={listRef}
			justify="start"
			p="0 24px 15px 24px"
			h="max(50px, 90%)"
			scrollbar
		>
			{options.map((item, i) => {
				if (showActiveItem && isBoolean(item.active) && !item.active) {
					return null;
				}

				const getCName = (v?: {
					companyId?: number;
					taxiServiceId?: number;
				}) => {
					if (!v) return "";
					if (!showCompanies) return "";
					if (v?.companyId) {
						const name = getCompanyName(v?.companyId);
						if (typeof name === "string") return name;
						if (typeof name === "object")
							return name?.[language] || "";
					}

					if (v?.taxiServiceId) {
						const companyId = getCompanyIdByTaxiServiceId(
							v.taxiServiceId,
						);

						if (companyId) {
							const name = getCompanyName(companyId);
							if (typeof name === "string") return name;
							if (typeof name === "object")
								return name?.[language] || "";
						}
					}
					return "";
				};
				const getTSName = (v?: { taxiServiceId?: number }) => {
					if (!v) return "";
					if (!showTaxiServices) return "";
					if (v?.taxiServiceId) {
						const name = getTaxiServiceName(v.taxiServiceId);
						if (name) {
							if (typeof name === "string") return `(${name})`;
							if (typeof name === "object")
								return name?.[language]
									? `(${name[language]})`
									: "";
						}
					}
					return "";
				};

				const companyName = getCName(item?.data);
				const taxiServicesName = getTSName(item?.data);

				return (
					<Item
						ref={(el) => (itemRefs.current[i] = el)}
						cursor="pointer"
						active={activeOption === i}
						key={item.value}
						onClick={() => {
							handleSelect(item);
							setWarning(false);
						}}
					>
						<CheckBoxWithText
							gap="10px"
							value={Boolean(
								selected.find(
									(option) => option.value === item.value,
								),
							)}
							onChange={(status) => {
								if (!status) {
									setSelected(
										selected.filter(
											(v) => v.value !== item.value,
										),
									);
								} else if (selectOnlyOne) {
									setSelected([item]);
								} else {
									setSelected([...selected, item]);
								}

								setWarning(false);
							}}
							iconBeforeText={item?.iconBeforeText}
							title={item?.name}
							w="100%"
							notActive={isBoolean(item.active) && !item.active}
							disabled={item.disabled}
						>
							{isBoolean(item.active) && !item.active && (
								<StyledSpan
									baseBorder
									p="0 5px"
									m="0 0 0 auto"
									colors={Colors.PLACEHOLDER}
								>
									{translateText.notActive}
								</StyledSpan>
							)}
							{item.labelRigth && (
								<StyledSpan
									baseBorder
									p="0 5px"
									m="0 0 0 auto"
									colors={Colors.PLACEHOLDER}
								>
									{item.labelRigth}
								</StyledSpan>
							)}

							{companyName && (
								<StyledSpan
									p="0 0 0 5px"
									m="0 0 0 auto"
									b={{
										left: `1px solid ${Colors.BORDER_GRAY}`,
									}}
								>
									{companyName}
								</StyledSpan>
							)}
							{taxiServicesName && (
								<StyledSpan>{taxiServicesName}</StyledSpan>
							)}
						</CheckBoxWithText>
					</Item>
				);
			})}
		</List>
	);
};

declare namespace ListCheckBoxes {
	interface Props {
		options: ListSelect.Option[];
		activeOption: number;
		selected: ListSelect.Option[];
		showActiveItem: boolean;
		translateText: {
			notActive: string;
		};
		selectOnlyOne: boolean;
		showTaxiServices?: boolean;
		showCompanies?: boolean;
		setSelected: (options: ListSelect.Option[]) => void;
		setWarning: (v: boolean) => void;
		handleSelect: (item: ListSelect.Option) => void;
		onScrollMaxVisible?: (index: number) => void;
	}
}

export default ListCheckBoxes;
